import React, {
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Flex,
	Grid,
	Typography,
} from 'antd';
import {
	useMutation,
	useQueryClient,
} from 'react-query';
import isEqual from 'lodash.isequal';
import {
	CargoContractSides,
	LaytimeSettlementTypes,
	LaytimeTerms,
	PortActionTypes,
} from '@shared/utils/constants';
import { capitalize } from '@shared/utils/string';
import { splitActionKey } from '@shared/utils/splitActionKey';
import { round } from '@shared/utils/math';
import normalize from '@shared/utils/normalizeNumber';
import type {
	CargoPortWithTurnDays,
	CargoWithPortsAndCalculationsJSON,
} from '@api/features/laytime/getCargosAndLaytimes';
import type { UpdateCargoPortRequest } from '@api/features/cargos/updateCargoPort';
import type { Port } from '@api/utils/ports';
import EditableDetails from '@client/components/EditableDetails/EditableDetails';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import {
	calculateLaytime,
	copyLaytimeToCargoRelet,
	updateCargo,
	updateCargoPort,
} from '@client/lib/api';
import CountryFlag from '@client/components/CountryFlag';
import { useSaveButton } from '@client/utils/hooks/useSaveButton';
import Button from '@client/components/Button';
import {
	getAveragedFields,
	getNonReversibleFields,
	getReversibleFields,
} from '../helpers/getKeyStatsFields';

export type NullableAction = 'loading' | 'discharging' | null;

const getTitle = (
	terms: LaytimeTerms | null,
	port: Port,
	portAndActionKey: string,
) => {
	const { action } = splitActionKey(portAndActionKey);
	const showAction = terms !== LaytimeTerms.NON_REVERSIBLE;

	return (
		<Flex gap={5}>
			<CountryFlag countryCode={port.countryCode} />
			<Typography.Text>
				{capitalize(port.name)}
				{showAction && ` - ${capitalize(action)}`}
			</Typography.Text>
		</Flex>
	);
};

export const getTimeAllowed = (
	cargoPort: Partial<CargoPortWithTurnDays>,
	loadingRate?: number | null | undefined,
	blQuantity?: number | null | undefined,
) => {
	let rate = loadingRate ?? cargoPort.loadingRate ?? 0;

	return round((blQuantity ?? cargoPort.blQuantity ?? 0) / rate, 3);
};

const getCargoPortEditValues = (
	cargoPort: CargoPortWithTurnDays,
) => ({
	blQuantity: cargoPort.blQuantity,
	loadingRate: cargoPort.loadingRate,
	includeDeadfreight: cargoPort.includeDeadfreight,
	calculationMethod: cargoPort.calculationMethod,
	turnDays: cargoPort.turnDays,
	laytimeFrom: cargoPort.laytimeFrom,
	laytimeTo: cargoPort.laytimeTo,
	estimatedTimeAllowed: cargoPort.estimatedTimeAllowed,
	estimatedTimeUsed: cargoPort.estimatedTimeUsed,
	demurrage: cargoPort.demurrage,
	despatch: cargoPort.despatch,
});

const getCargoEditValues = (
	cargo: CargoWithPortsAndCalculationsJSON,
) => ({
	type: cargo.type,
	demurrage: cargo.demurrage,
	despatch: cargo.despatch,
	demurrageLoading: cargo.demurrageLoading,
	despatchLoading: cargo.despatchLoading,
	demurrageDischarge: cargo.demurrageDischarge,
	despatchDischarge: cargo.despatchDischarge,
	laytimeTerms: cargo.laytimeTerms,
	despatchHalfDemurrage: cargo.despatchHalfDemurrage !== false,
});

type Props = {
	cargo: CargoWithPortsAndCalculationsJSON;
	handleCalculateLaytime: () => Promise<void> | null;
	exclusionsChanged: boolean;
	setExclusionsChanged: React.Dispatch<React.SetStateAction<boolean>>;
	selectedCargoPort?: CargoPortWithTurnDays;
};

const KeyStats = ({
	cargo,
	selectedCargoPort,
	handleCalculateLaytime,
	exclusionsChanged,
	setExclusionsChanged,
}: Props) => {
	const [cargoState, setCargoState] = useState(cargo);
	const [cargoPortState, setCargoPortState] = useState(selectedCargoPort);
	const [hasSavedAndCalculated, setHasSavedAndCalculated] = useState(false);
	const [queueSave, setQueueSave] = useState(false);
	const screens = Grid.useBreakpoint();
	const queryClient = useQueryClient();

	const getDiff = <Values extends object>(state: Values, changes: Partial<Values>) => {
		const changeObj: Record<string, any> = {};

		Object.entries(changes).forEach(([key, value]) => {
			if (!isEqual(state[key as keyof typeof state], changes[key as keyof typeof state])) {
				changeObj[key as keyof typeof changeObj] = value;
			}
		});

		return changeObj as Values;
	};

	const hasCargoPortChanges = useMemo(() => selectedCargoPort != null &&
	    cargoPortState != null &&
	    Object.values(getDiff(selectedCargoPort, cargoPortState)).length > 0, 
	[cargoPortState, selectedCargoPort]
	);

	const hasCargoChanges = useMemo(() => Object.values(getDiff(cargo, cargoState)).length > 0, [cargo, cargoState]);
	const hasUnsavedChanges = useMemo(() => hasCargoChanges || hasCargoPortChanges, [hasCargoChanges, hasCargoPortChanges]);

	const copyDisabledReason = useMemo(() => {
		if (hasUnsavedChanges) {
			return 'You have unsaved changes. Save & calculate first.';
		}

		return undefined;
	}, [hasSavedAndCalculated, hasUnsavedChanges]);

	useEffect(() => {
		if (!selectedCargoPort || !cargo) {
			return;
		}

		const isDifferentSelection =
			cargo.id !== cargoState?.id ||
			selectedCargoPort.id !== cargoPortState?.id;

		setCargoState(cargo);
		setCargoPortState(selectedCargoPort);
		if (isDifferentSelection) {
			setHasSavedAndCalculated(false);
		}
	}, [selectedCargoPort, cargo]);

	const action = useMemo(() => {
		if (!cargoPortState) {
			return PortActionTypes.LOADING;
		}

		const { action: parsedAction } = splitActionKey(cargoPortState.portAndActionKey);

		return parsedAction;
	}, [cargoPortState]);

	const settlementType = useMemo(() => {
		if (!cargo || !selectedCargoPort) {
			return LaytimeSettlementTypes.DEMURRAGE;
		}

		switch (cargo.laytimeTerms) {
			case LaytimeTerms.AVERAGED: {
				return action === PortActionTypes.LOADING ?
					cargo.settlementTypeLoading :
					cargo.settlementTypeDischarging;
			}

			case LaytimeTerms.NON_REVERSIBLE: {
				return selectedCargoPort.settlementType;
			}

			case LaytimeTerms.REVERSIBLE: {
				return cargo.settlementType;
			}
		}

		return LaytimeSettlementTypes.DEMURRAGE;
	}, [action, cargo, selectedCargoPort]);
	
	const handleCargoChange = (values: Partial<CargoWithPortsAndCalculationsJSON>, shouldSave?: boolean) => {
		if (!cargoState) {
			return;
		}

		const mergedForDhd = {
			...cargoState,
			...values,
		};

		const useHalf = mergedForDhd.despatchHalfDemurrage !== false;

		if ('demurrage' in values && useHalf) {
			values.despatch = (values.demurrage ?? 0) / 2;
		}

		if ('demurrageLoading' in values && useHalf) {
			values.despatchLoading = (values.demurrageLoading ?? 0) / 2;
		}

		if ('demurrageDischarge' in values && useHalf) {
			values.despatchDischarge = (values.demurrageDischarge ?? 0) / 2;
		}

		setCargoState((prev) => ({
			...prev,
			...values,
		}));

		if (shouldSave) {
			setQueueSave(true);
		}
	};

	const handleCargoPortChange = (values: Partial<UpdateCargoPortRequest['attributes']>, shouldSave?: boolean) => {
		if (!cargoPortState) {
			return;
		}

		const changes = getDiff(cargoPortState, values);

		if ('includeDeadfreight' in changes) {
			// @ts-ignore - return value is html element
			const value = changes.includeDeadfreight.target.checked;
			changes.includeDeadfreight = value;
		}

		let calculatedTimeAllowed = changes.estimatedTimeAllowed;

		if (
			'blQuantity' in changes ||
			'loadingRate' in changes ||
			'includeDeadfreight' in changes ||
			'laytimeQuantityOverride' in changes
		) {
			let quantity = changes.laytimeQuantityOverride ??
				cargoPortState.laytimeQuantityOverride ??
				changes.blQuantity ??
				cargoPortState.blQuantity;

			if (changes.includeDeadfreight != null && !changes.includeDeadfreight) {
				quantity = cargoPortState.blQuantity;
				changes.laytimeQuantityOverride = null;
			}

			calculatedTimeAllowed = getTimeAllowed(
				cargoPortState,
				values.loadingRate,
				quantity,
			);

			changes.estimatedTimeAllowed = normalize(calculatedTimeAllowed);
		}

		if ('demurrage' in changes && cargoState.despatchHalfDemurrage !== false) {
			changes.despatch = (changes.demurrage ?? 0) / 2;
		}

		setCargoPortState((prev) => ({
			...prev,
			...changes,
		} as CargoPortWithTurnDays));

		if (shouldSave) {
			setQueueSave(true);
		}
	};

	const onSave = async () => {
		const hadChanges = hasUnsavedChanges;
		await onUpdateCargo();
		await onUpdateCargoPort();
		await handleCalculateLaytime();
		setExclusionsChanged(false);
		setHasSavedAndCalculated(hadChanges);
	};

	useEffect(() => {
		if (queueSave && hasUnsavedChanges) {
			onSave();
			setQueueSave(false);
		}
	}, [queueSave, hasUnsavedChanges]);

	const {
		keyItems,
		cargoPortItems,
	} = useMemo(() => {
		if (!cargoState) {
			return {
				keyItems: null,
				cargoPortItems: null,
			};
		}

		const { laytimeTerms } = cargoState;

		switch (laytimeTerms) {
			case LaytimeTerms.REVERSIBLE: {
				if (!cargoPortState) {
					return {
						keyItems: null,
						cargoPortItems: null,
					};
				}

				return getReversibleFields(
					cargoState,
					cargoPortState,
					handleCargoChange,
					handleCargoPortChange,
					!screens.xxl,
					settlementType,
				);
			}

			case LaytimeTerms.AVERAGED: {
				if (!cargoPortState) {
					return {
						keyItems: null,
						cargoPortItems: null,
					};
				}

				return getAveragedFields(
					cargoState,
					cargoPortState,
					action,
					handleCargoChange,
					handleCargoPortChange,
					!screens.xxl,
					settlementType,
				);
			}

			default:
			case LaytimeTerms.NON_REVERSIBLE: {
				if (!cargoPortState) {
					return {
						keyItems: null,
						cargoPortItems: null,
					};
				}

				return getNonReversibleFields(
					cargoState,
					cargoPortState,
					handleCargoChange,
					handleCargoPortChange,
					!screens.xxl,
					settlementType,
				);
			}
		}
	}, [
		cargoState,
		cargoPortState,
		action,
		screens,
		settlementType,
	]);

	const updateCargoMutation = useMutation(
		updateCargo,
		{
			onSuccess: () => {
				showSuccessNotification('Laytime updated');
				queryClient.invalidateQueries(['portDAs']);
			},
		},
	);

	const updateCargoPortMutation = useMutation(
		updateCargoPort,
		{
			onSuccess: async () => {
				showSuccessNotification('Laytime updated');
				queryClient.invalidateQueries(['portDAs']);
			},
		},
	);

	const onUpdateCargoPort = async () => {
		if (!cargoPortState || !selectedCargoPort) {
			return;
		}

		const diff = getDiff(selectedCargoPort, cargoPortState);

		if (Object.values(diff).length === 0) {
			return;
		}

		await updateCargoPortMutation.mutateAsync({
			cargoId: cargoState.id,
			cargoPortId: cargoPortState.id,
			attributes: diff as UpdateCargoPortRequest['attributes'],
		});
	};

	const onUpdateCargo = async () => {
		if (!cargoPortState || !cargo) {
			return;
		}

		const diff = getDiff(cargo, cargoState);

		if (Object.values(diff).length === 0) {
			return;
		}

		const attributes = 'laytimeTerms' in diff ?
			{
				...diff,
				estimatedTimeUsed: cargoState.estimatedTimeUsed,
				estimatedTimeUsedLoading: cargoState.estimatedTimeUsedLoading,
				estimatedTimeUsedDischarging: cargoState.estimatedTimeUsedDischarging,
			} :
			diff;

		// @ts-ignore
		await updateCargoMutation.mutateAsync({ cargoId: cargoState.id, attributes });
	};
	
	const hasChanges = useMemo(() => {
		const cargoHasChanges = Object.values(getDiff(cargo, cargoState)).length > 0;

		let cargoPortHasChanges = false;

		if (selectedCargoPort && cargoPortState) {
			cargoPortHasChanges = Object.values(getDiff(selectedCargoPort, cargoPortState)).length > 0;
		}

		return cargoHasChanges || cargoPortHasChanges || exclusionsChanged;
	}, [selectedCargoPort, cargoPortState, cargo, cargoState, exclusionsChanged]);

	const button = useSaveButton({
		onSave,
		hasPendingChanges: hasChanges,
		loading: false,
		title: 'Save & Calculate'
	});
	
	const copyLaytimeMutation = useMutation(
		copyLaytimeToCargoRelet,
		{
			onSuccess: async () => {
				showSuccessNotification('Laytime copied to cargo relet');
				await handleCalculateLaytime();
				if (cargoState.reletCargoId) {
					await calculateLaytime(cargoState.reletCargoId);
				}

				await queryClient.invalidateQueries(['cargos']);
				await queryClient.invalidateQueries(['laytime']);
				await queryClient.invalidateQueries(['voyageDetails']);
				await queryClient.invalidateQueries(['voyageInvoices']);
			},
		},
	);

	const handleCopyLaytime = async () => {
		if (!cargoState?.id) {
			return;
		}

		await copyLaytimeMutation.mutateAsync(cargoState.id);
	};

	if (!cargoPortItems || !keyItems || !cargoPortState) {
		return null;
	}
	
	return (
		<Flex gap={10} vertical>
			<Flex gap={5}>
				{cargoState.contractSide === CargoContractSides.VC_OUT && cargoState.reletCargoId != null ?
					<Button
						onClick={handleCopyLaytime}
						loading={copyLaytimeMutation.isLoading}
						disabled={copyDisabledReason != null}
						disabledTooltip={copyDisabledReason}
					>
						Copy laytime to relet
					</Button> : null}
				{button}
			</Flex>
			<EditableDetails
				labelWidth={180}
				title={null}
				items={keyItems}
				editValues={getCargoEditValues(cargoState)}
				onEditValuesChange={handleCargoChange}
				editing
				showCancelButton={false}
				showSaveButton={false}
				blockNavigationOverride={() => {
					if (!cargo) {
						return false;
					}

					const changes = getDiff(cargo, cargoState);

					return Object.values(changes).length > 0;
				}}
			/>
			<EditableDetails
				labelWidth={180}
				title={getTitle(cargo.laytimeTerms, cargoPortItems.port, cargoPortItems.portAndActionKey)}
				items={cargoPortItems.items}
				onEditValuesChange={handleCargoPortChange}
				editValues={getCargoPortEditValues(cargoPortState)}
				editing
				showCancelButton={false}
				showSaveButton={false}
				enableAutoSave={false}
				blockNavigationOverride={() => {
					if (!selectedCargoPort) {
						return false;
					}

					const changes = getDiff(selectedCargoPort, cargoPortState);

					return Object.values(changes).length > 0;
				}}
			/>
		</Flex>
	);
};

export default KeyStats;
